import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Fade,
  InputAdornment,
  Snackbar,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import certificadosService from "../../api/certificadosService";
import CertificadosHeader from "./components/CertificadosHeader";
import ServicioList from "./components/ServicioList";
import CertificadoPreview from "./components/CertificadoPreview";
import { TABS, NORMAS_SUGERIDAS } from "./constants";
import {
  guardarEmpresa,
  leerEmpresaGuardada,
  normasToText,
  textToNormas,
  toCertificado,
} from "./utils";
import { renderQrSvg, qrMuestraValue } from "./qr";

export default function Certificados() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [tab, setTab] = useState(TABS.PENDIENTES);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [emitiendo, setEmitiendo] = useState(false);
  const [pendientes, setPendientes] = useState([]);
  const [emitidos, setEmitidos] = useState([]);
  const [selectedPendiente, setSelectedPendiente] = useState(null);
  const [selectedEmitido, setSelectedEmitido] = useState(null);
  const [empresa, setEmpresa] = useState(leerEmpresaGuardada);
  const [normasText, setNormasText] = useState(normasToText(NORMAS_SUGERIDAS));
  const [qrSvg, setQrSvg] = useState();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await certificadosService.getByMonth(year, month);
      const data = res.data.data || { pendientes: [], emitidos: [] };
      setPendientes(data.pendientes || []);
      setEmitidos(data.emitidos || []);
    } catch (err) {
      console.error("Error cargando certificados", err);
      showSnackbar(
        err.response?.data?.message || "No se pudieron cargar los certificados",
        "error"
      );
      setPendientes([]);
      setEmitidos([]);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
    setSelectedPendiente(null);
    setSelectedEmitido(null);
  }, [load]);

  useEffect(() => {
    guardarEmpresa(empresa);
  }, [empresa]);

  const matchSearch = useCallback(
    (items) => {
      if (!search.trim()) return items;
      const q = search.toLowerCase();
      return items.filter(
        (item) =>
          item.cliente?.toLowerCase().includes(q) ||
          item.lugar?.toLowerCase().includes(q) ||
          item.servicio?.toLowerCase().includes(q) ||
          item.folio?.toLowerCase().includes(q) ||
          item.tecnico?.nombre?.toLowerCase().includes(q)
      );
    },
    [search]
  );

  const pendientesFiltrados = useMemo(
    () => matchSearch(pendientes),
    [pendientes, matchSearch]
  );
  const emitidosFiltrados = useMemo(
    () => matchSearch(emitidos),
    [emitidos, matchSearch]
  );

  const certificado = useMemo(() => {
    if (tab === TABS.EMITIDOS && selectedEmitido) {
      return toCertificado(selectedEmitido);
    }
    if (tab === TABS.PENDIENTES && selectedPendiente) {
      return toCertificado(selectedPendiente, empresa);
    }
    return null;
  }, [tab, selectedEmitido, selectedPendiente, empresa]);

  useEffect(() => {
    if (tab === TABS.PENDIENTES && selectedPendiente) {
      setNormasText(normasToText(selectedPendiente.normas || NORMAS_SUGERIDAS));
    }
  }, [selectedPendiente, tab]);

  useEffect(() => {
    let vigente = true;
    const folio = certificado?.folio;
    const valor = qrMuestraValue(
      folio && !folio.includes("—") && !folio.startsWith("Se asignará")
        ? folio
        : "MUESTRA"
    );

    renderQrSvg(valor).then(
      (svg) => {
        if (vigente) setQrSvg(svg);
      },
      () => {
        if (vigente) setQrSvg(undefined);
      }
    );

    return () => {
      vigente = false;
    };
  }, [certificado?.folio]);

  const handleEmitir = async () => {
    if (!selectedPendiente?.pestReportId) {
      showSnackbar("Este servicio no tiene reporte de plagas", "error");
      return;
    }

    setEmitiendo(true);
    try {
      const res = await certificadosService.emitir({
        pestReportId: selectedPendiente.pestReportId,
        normas: textToNormas(normasText),
        empresa,
      });
      const emitido = res.data.data;
      showSnackbar("Certificado emitido", "success");
      await load();
      setTab(TABS.EMITIDOS);
      setSelectedEmitido(emitido);
      setSelectedPendiente(null);
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || "No se pudo emitir el certificado",
        "error"
      );
    } finally {
      setEmitiendo(false);
    }
  };

  const certificadoVista = useMemo(() => {
    if (!certificado) return null;
    if (tab === TABS.PENDIENTES) {
      return {
        ...certificado,
        normas: textToNormas(normasText),
        empresa: {
          ...certificado.empresa,
          licencia: empresa.licencia || "—",
          licenciaVigencia: empresa.licenciaVigencia || "—",
        },
      };
    }
    return certificado;
  }, [certificado, tab, normasText, empresa]);

  return (
    <Box sx={{ bgcolor: "#f8f9fc", minHeight: "100%", width: "100%" }}>
      <Container maxWidth={false} disableGutters sx={{ width: "100%" }}>
        <Fade in timeout={400}>
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: { xs: "16px", sm: "20px" },
              border: "1px solid #e8eaef",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              p: { xs: 2.5, sm: 3, md: 4 },
            }}
          >
            <CertificadosHeader
              month={month}
              year={year}
              onMonthChange={setMonth}
              onYearChange={setYear}
            />

            <Box className="no-print" sx={{ mt: 2.5, mb: 2 }}>
              <Tabs
                value={tab}
                onChange={(_e, value) => setTab(value)}
                sx={{
                  minHeight: 42,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    minHeight: 42,
                  },
                }}
              >
                <Tab
                  value={TABS.PENDIENTES}
                  label={`Por emitir (${pendientes.length})`}
                />
                <Tab
                  value={TABS.EMITIDOS}
                  label={`Emitidos (${emitidos.length})`}
                />
              </Tabs>
            </Box>

            <TextField
              className="no-print"
              size="small"
              fullWidth
              placeholder="Buscar por cliente, sucursal, folio o técnico…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  bgcolor: "#fafbfc",
                  fontSize: "0.875rem",
                  "& fieldset": { borderColor: "#e8eaef" },
                },
              }}
            />

            {loading ? (
              <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gap: 3,
                  gridTemplateColumns: { lg: "minmax(0, 380px) minmax(0, 1fr)" },
                  alignItems: "start",
                }}
              >
                <Box className="no-print">
                  {tab === TABS.PENDIENTES ? (
                    <ServicioList
                      items={pendientesFiltrados}
                      selectedId={selectedPendiente?.pestReportId}
                      onSelect={setSelectedPendiente}
                    />
                  ) : (
                    <ServicioList
                      items={emitidosFiltrados}
                      selectedId={selectedEmitido?.folio}
                      onSelect={setSelectedEmitido}
                      emitidos
                    />
                  )}
                </Box>

                <Box sx={{ position: { lg: "sticky" }, top: 16 }}>
                  <CertificadoPreview
                    certificado={certificadoVista}
                    qrSvg={qrSvg}
                    emitido={tab === TABS.EMITIDOS}
                    emitiendo={emitiendo}
                    onEmitir={handleEmitir}
                    empresa={empresa}
                    onEmpresaChange={setEmpresa}
                    normasText={normasText}
                    onNormasChange={setNormasText}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Container>

      <Snackbar
        className="no-print"
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px", fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
